import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useStoreConfig } from '@/contexts/StoreConfigContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2, Palette, Store, Mail, Share2, Search, BarChart3, Plug, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

function FieldGroup({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="font-sans text-xs font-medium block" style={{ color: 'rgba(26,26,26,0.6)' }}>{label}</label>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg border cursor-pointer p-0.5" style={{ borderColor: 'rgba(26,26,26,0.1)' }} />
      <div className="flex-1">
        <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>{label}</p>
        <p className="font-sans text-sm font-mono" style={{ color: '#1A1A1A' }}>{value}</p>
      </div>
    </div>
  );
}

function MaskedInput({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input data-testid={testId} type={show ? 'text' : 'password'} className={inputCls} value={value || ''} onChange={onChange} placeholder={placeholder} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1">
        {show ? <EyeOff size={14} style={{ color: '#999' }} /> : <Eye size={14} style={{ color: '#999' }} />}
      </button>
    </div>
  );
}

function StatusDot({ configured }) {
  return (
    <div className="flex items-center gap-1.5">
      {configured ? <CheckCircle size={14} style={{ color: '#16a34a' }} /> : <XCircle size={14} style={{ color: '#dc2626' }} />}
      <span className="font-sans text-xs" style={{ color: configured ? '#16a34a' : '#999' }}>
        {configured ? 'Connected' : 'Not configured'}
      </span>
    </div>
  );
}

const inputCls = "h-10 rounded-none border-[rgba(26,26,26,0.12)] focus:ring-[#C4956A] focus:border-[#C4956A] font-sans text-sm";

export default function SettingsPage() {
  const { config, loading: configLoading, updateConfig } = useStoreConfig();
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState('');

  useEffect(() => {
    if (config) setForm(JSON.parse(JSON.stringify(config)));
  }, [config]);

  if (configLoading || !form) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#C4956A' }} />
    </div>
  );

  const set = (path, value) => {
    setForm(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateConfig(form);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const testConnection = async (type) => {
    setTesting(type);
    try {
      // Save first, then test
      await updateConfig(form);
      if (type === 'openrouter') {
        await api.post('/ai/chat', { message: 'ping', conversation_history: [] });
        toast.success('OpenRouter: Connected successfully');
      } else if (type === 'sendgrid') {
        toast.success('SendGrid: Configuration saved (test send via Notifications page)');
      } else if (type === 'twilio') {
        toast.success('Twilio: Configuration saved (test send via Notifications page)');
      } else {
        toast.success(`${type}: Configuration saved`);
      }
    } catch (e) {
      toast.error(`${type}: Connection failed — ${e.response?.data?.detail || e.message}`);
    }
    setTesting('');
  };

  const integrations = form.integrations || {};

  return (
    <motion.div data-testid="admin-settings-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Settings</h1>
        <Button data-testid="settings-save-button" onClick={handleSave} disabled={saving}
          className="h-10 px-6 rounded-none font-sans text-sm font-medium"
          style={{ backgroundColor: '#E85D3A', color: '#FFFFFF' }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
          Save
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="mb-6 h-auto p-0 bg-transparent border-b rounded-none w-full justify-start gap-0" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
          {[
            { value: 'general', icon: Store, label: 'General' },
            { value: 'branding', icon: Palette, label: 'Branding' },
            { value: 'contact', icon: Mail, label: 'Contact' },
            { value: 'social', icon: Share2, label: 'Social' },
            { value: 'seo', icon: Search, label: 'SEO' },
            { value: 'integrations', icon: Plug, label: 'Integrations' },
          ].map(tab => (
            <TabsTrigger key={tab.value} value={tab.value} data-testid={`settings-tab-${tab.value}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E85D3A] data-[state=active]:text-[#1A1A1A] font-sans text-xs px-4 py-3 gap-1.5">
              <tab.icon size={14} /> {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="rounded-lg border p-6 md:p-8" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.06)' }}>
          <TabsContent value="general" className="mt-0 space-y-5">
            <FieldGroup label="Store Name"><Input data-testid="settings-store-name" className={inputCls} value={form.store_name || ''} onChange={e => set('store_name', e.target.value)} /></FieldGroup>
            <FieldGroup label="Tagline"><Input className={inputCls} value={form.tagline || ''} onChange={e => set('tagline', e.target.value)} /></FieldGroup>
            <FieldGroup label="Description"><textarea className={`${inputCls} w-full min-h-[100px] p-3 border`}
              style={{ borderColor: 'rgba(26,26,26,0.12)', backgroundColor: 'transparent' }}
              value={form.description || ''} onChange={e => set('description', e.target.value)} /></FieldGroup>
            <FieldGroup label="Announcement Messages (one per line)">
              <textarea className={`${inputCls} w-full min-h-[80px] p-3 border`}
                style={{ borderColor: 'rgba(26,26,26,0.12)', backgroundColor: 'transparent' }}
                value={(form.announcement_messages || []).join('\n')}
                onChange={e => set('announcement_messages', e.target.value.split('\n').filter(Boolean))}
                placeholder="50% OFF&#10;FREE SHIPPING ALL OVER UK&#10;SUPER SALE | UP TO 50% OFF" />
            </FieldGroup>
          </TabsContent>

          <TabsContent value="branding" className="mt-0 space-y-6">
            <div>
              <h3 className="font-sans text-sm font-bold mb-4" style={{ color: '#1A1A1A' }}>Colors</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ColorField label="Primary" value={form.colors?.primary} onChange={v => set('colors.primary', v)} />
                <ColorField label="Accent" value={form.colors?.accent} onChange={v => set('colors.accent', v)} />
                <ColorField label="Background" value={form.colors?.background} onChange={v => set('colors.background', v)} />
                <ColorField label="Text" value={form.colors?.text} onChange={v => set('colors.text', v)} />
                <ColorField label="CTA Button" value={form.colors?.cta_button} onChange={v => set('colors.cta_button', v)} />
                <ColorField label="CTA Text" value={form.colors?.cta_text} onChange={v => set('colors.cta_text', v)} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contact" className="mt-0 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldGroup label="Email"><Input className={inputCls} value={form.contact?.email || ''} onChange={e => set('contact.email', e.target.value)} /></FieldGroup>
              <FieldGroup label="Phone"><Input className={inputCls} value={form.contact?.phone || ''} onChange={e => set('contact.phone', e.target.value)} /></FieldGroup>
              <FieldGroup label="Address"><Input className={inputCls} value={form.contact?.address || ''} onChange={e => set('contact.address', e.target.value)} /></FieldGroup>
              <FieldGroup label="City"><Input className={inputCls} value={form.contact?.city || ''} onChange={e => set('contact.city', e.target.value)} /></FieldGroup>
              <FieldGroup label="State"><Input className={inputCls} value={form.contact?.state || ''} onChange={e => set('contact.state', e.target.value)} /></FieldGroup>
              <FieldGroup label="Country"><Input className={inputCls} value={form.contact?.country || ''} onChange={e => set('contact.country', e.target.value)} /></FieldGroup>
            </div>
          </TabsContent>

          <TabsContent value="social" className="mt-0 space-y-5">
            {['instagram', 'facebook', 'twitter', 'tiktok', 'youtube'].map(s => (
              <FieldGroup key={s} label={s.charAt(0).toUpperCase() + s.slice(1)}><Input className={inputCls} value={form.social?.[s] || ''} onChange={e => set(`social.${s}`, e.target.value)} /></FieldGroup>
            ))}
          </TabsContent>

          <TabsContent value="seo" className="mt-0 space-y-5">
            <FieldGroup label="Meta Title"><Input className={inputCls} value={form.meta_title || ''} onChange={e => set('meta_title', e.target.value)} /></FieldGroup>
            <FieldGroup label="Meta Description"><textarea className={`${inputCls} w-full min-h-[80px] p-3 border`}
              style={{ borderColor: 'rgba(26,26,26,0.12)', backgroundColor: 'transparent' }}
              value={form.meta_description || ''} onChange={e => set('meta_description', e.target.value)} /></FieldGroup>
            <FieldGroup label="GA4 Measurement ID"><Input className={inputCls} value={form.ga4_measurement_id || ''} onChange={e => set('ga4_measurement_id', e.target.value)} placeholder="G-XXXXXXXXXX" /></FieldGroup>
            <FieldGroup label="Google Ads Conversion ID"><Input className={inputCls} value={form.google_ads_conversion_id || ''} onChange={e => set('google_ads_conversion_id', e.target.value)} /></FieldGroup>
            <FieldGroup label="Google Ads Conversion Label"><Input className={inputCls} value={form.google_ads_conversion_label || ''} onChange={e => set('google_ads_conversion_label', e.target.value)} /></FieldGroup>
            <FieldGroup label="Facebook Pixel ID"><Input className={inputCls} value={form.facebook_pixel_id || ''} onChange={e => set('facebook_pixel_id', e.target.value)} /></FieldGroup>
          </TabsContent>

          {/* INTEGRATIONS TAB */}
          <TabsContent value="integrations" data-testid="integrations-tab" className="mt-0 space-y-8">
            {/* Stripe */}
            <div className="p-5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
              <div className="flex items-center justify-between mb-4">
                <div><h3 className="font-sans text-sm font-bold" style={{ color: '#000' }}>Stripe (Payments)</h3></div>
                <StatusDot configured={!!integrations.stripe_secret_key && !integrations.stripe_secret_key.startsWith('placeholder')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldGroup label="Publishable Key">
                  <MaskedInput value={integrations.stripe_publishable_key} onChange={e => set('integrations.stripe_publishable_key', e.target.value)} placeholder="pk_test_..." />
                </FieldGroup>
                <FieldGroup label="Secret Key">
                  <MaskedInput value={integrations.stripe_secret_key} onChange={e => set('integrations.stripe_secret_key', e.target.value)} placeholder="sk_test_..." />
                </FieldGroup>
                <FieldGroup label="Webhook Secret">
                  <MaskedInput value={integrations.stripe_webhook_secret} onChange={e => set('integrations.stripe_webhook_secret', e.target.value)} placeholder="whsec_..." />
                </FieldGroup>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={integrations.stripe_test_mode !== false} onCheckedChange={v => set('integrations.stripe_test_mode', v)} />
                  <span className="font-sans text-xs" style={{ color: '#666' }}>Test Mode</span>
                </div>
              </div>
            </div>

            {/* SendGrid */}
            <div className="p-5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-sans text-sm font-bold" style={{ color: '#000' }}>SendGrid (Email)</h3>
                <StatusDot configured={!!integrations.sendgrid_api_key && !integrations.sendgrid_api_key.startsWith('placeholder')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldGroup label="API Key">
                  <MaskedInput value={integrations.sendgrid_api_key} onChange={e => set('integrations.sendgrid_api_key', e.target.value)} placeholder="SG...." />
                </FieldGroup>
                <FieldGroup label="From Email"><Input className={inputCls} value={integrations.sendgrid_from_email || ''} onChange={e => set('integrations.sendgrid_from_email', e.target.value)} placeholder="orders@store.com" /></FieldGroup>
                <FieldGroup label="From Name"><Input className={inputCls} value={integrations.sendgrid_from_name || ''} onChange={e => set('integrations.sendgrid_from_name', e.target.value)} placeholder="Store Name" /></FieldGroup>
              </div>
            </div>

            {/* Twilio */}
            <div className="p-5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-sans text-sm font-bold" style={{ color: '#000' }}>Twilio (SMS)</h3>
                <StatusDot configured={!!integrations.twilio_account_sid && !integrations.twilio_account_sid.startsWith('placeholder')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldGroup label="Account SID"><MaskedInput value={integrations.twilio_account_sid} onChange={e => set('integrations.twilio_account_sid', e.target.value)} placeholder="AC..." /></FieldGroup>
                <FieldGroup label="Auth Token"><MaskedInput value={integrations.twilio_auth_token} onChange={e => set('integrations.twilio_auth_token', e.target.value)} /></FieldGroup>
                <FieldGroup label="From Phone Number"><Input className={inputCls} value={integrations.twilio_from_number || ''} onChange={e => set('integrations.twilio_from_number', e.target.value)} placeholder="+15551234567" /></FieldGroup>
              </div>
            </div>

            {/* OpenRouter */}
            <div className="p-5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-sans text-sm font-bold" style={{ color: '#000' }}>OpenRouter (AI)</h3>
                <StatusDot configured={!!integrations.openrouter_api_key} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldGroup label="API Key"><MaskedInput value={integrations.openrouter_api_key} onChange={e => set('integrations.openrouter_api_key', e.target.value)} placeholder="sk-or-..." /></FieldGroup>
              </div>
              <Button variant="outline" size="sm" onClick={() => testConnection('openrouter')} disabled={!!testing}
                className="mt-3 rounded-none font-sans text-xs gap-1" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
                {testing === 'openrouter' ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />} Test Connection
              </Button>
            </div>

            {/* Analytics */}
            <div className="p-5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
              <h3 className="font-sans text-sm font-bold mb-4" style={{ color: '#000' }}>Analytics</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldGroup label="GA4 Measurement ID"><Input className={inputCls} value={form.ga4_measurement_id || ''} onChange={e => set('ga4_measurement_id', e.target.value)} placeholder="G-XXXXXXXXXX" /></FieldGroup>
                <FieldGroup label="Google Ads Conversion ID"><Input className={inputCls} value={form.google_ads_conversion_id || ''} onChange={e => set('google_ads_conversion_id', e.target.value)} /></FieldGroup>
                <FieldGroup label="Google Ads Conversion Label"><Input className={inputCls} value={form.google_ads_conversion_label || ''} onChange={e => set('google_ads_conversion_label', e.target.value)} /></FieldGroup>
                <FieldGroup label="Facebook Pixel ID"><Input className={inputCls} value={form.facebook_pixel_id || ''} onChange={e => set('facebook_pixel_id', e.target.value)} /></FieldGroup>
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </motion.div>
  );
}
